import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Shield, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  AlertTriangle,
  Lock,
  Info,
  X,
  Key
} from 'lucide-react';

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  border-radius: 25px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0 0 10px 0;
  color: #ffd700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  margin: 0;
  line-height: 1.5;
`;

const TicketInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 20px;
  margin-bottom: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: center;
`;

const TicketClass = styled.div<{ class: string }>`
  background: ${props => {
    switch (props.class) {
      case 'first': return 'linear-gradient(45deg, #ffd700, #ffed4e)';
      case 'second': return 'linear-gradient(45deg, #c0c0c0, #e5e5e5)';
      case 'third': return 'linear-gradient(45deg, #cd7f32, #daa520)';
      case 'premium': return 'linear-gradient(45deg, #9c27b0, #e1bee7)';
      case 'vip': return 'linear-gradient(45deg, #ff5722, #ff8a65)';
      default: return 'linear-gradient(45deg, #2196f3, #64b5f6)';
    }
  }};
  color: ${props => props.class === 'second' ? '#333' : 'white'};
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  text-transform: uppercase;
  display: inline-block;
  margin-bottom: 15px;
`;

const TicketAmount = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #4caf50;
  margin-bottom: 10px;
`;

const PinSection = styled.div`
  margin-bottom: 30px;
`;

const PinLabel = styled.label`
  display: block;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: white;
  text-align: center;
`;

const PinInputContainer = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-bottom: 20px;
`;

const PinInput = styled.input<{ hasError: boolean; isFocused: boolean }>`
  width: 60px;
  height: 60px;
  background: ${props => props.hasError ? 'rgba(244, 67, 54, 0.1)' : 'rgba(255, 255, 255, 0.1)'};
  border: 2px solid ${props => {
    if (props.hasError) return '#f44336';
    if (props.isFocused) return '#4caf50';
    return 'rgba(255, 255, 255, 0.3)';
  }};
  border-radius: 15px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #4caf50;
    background: rgba(255, 255, 255, 0.15);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const PinToggle = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 auto;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const Instructions = styled.div`
  background: rgba(33, 150, 243, 0.1);
  border: 1px solid rgba(33, 150, 243, 0.3);
  border-radius: 15px;
  padding: 20px;
  margin-bottom: 20px;
  color: #2196f3;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const SecurityTips = styled.div`
  background: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 15px;
  padding: 20px;
  margin-bottom: 20px;
  color: #ff9800;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const ErrorMessage = styled.div`
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 15px;
  padding: 15px;
  margin-bottom: 20px;
  color: #f44336;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 15px;
  padding: 15px;
  margin-bottom: 20px;
  color: #4caf50;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 0.9rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'primary' 
    ? 'linear-gradient(45deg, #4caf50, #8bc34a)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  border: 1px solid ${props => props.variant === 'primary' 
    ? '#4caf50' 
    : 'rgba(255, 255, 255, 0.3)'
  };
  color: white;
  padding: 15px 30px;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 8px 25px rgba(76, 175, 80, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

interface PinSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: any;
  onPinSet: (ticketId: string, pin: string) => Promise<boolean>;
  isChangingPin?: boolean;
}

const PinSetupModal: React.FC<PinSetupModalProps> = ({
  isOpen,
  onClose,
  ticket,
  onPinSet,
  isChangingPin = false
}) => {
  const [pin, setPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState<'pin' | 'confirm'>('pin');
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const confirmInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (isOpen) {
      setPin(['', '', '', '']);
      setConfirmPin(['', '', '', '']);
      setError('');
      setSuccess(false);
      setCurrentStep('pin');
      setIsProcessing(false);
      // Focus first input
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 100);
    }
  }, [isOpen]);

  const handlePinChange = (index: number, value: string, isConfirm = false) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      if (isConfirm) {
        const newConfirmPin = [...confirmPin];
        newConfirmPin[index] = value;
        setConfirmPin(newConfirmPin);
        
        // Auto-focus next input
        if (value && index < 3) {
          setTimeout(() => {
            if (confirmInputRefs.current[index + 1]) {
              confirmInputRefs.current[index + 1]?.focus();
            }
          }, 100);
        }
      } else {
        const newPin = [...pin];
        newPin[index] = value;
        setPin(newPin);
        
        // Auto-focus next input
        if (value && index < 3) {
          setTimeout(() => {
            if (inputRefs.current[index + 1]) {
              inputRefs.current[index + 1]?.focus();
            }
          }, 100);
        }
      }
      setError('');
    }
  };

  const handlePinKeyDown = (index: number, e: React.KeyboardEvent, isConfirm = false) => {
    if (e.key === 'Backspace') {
      if (isConfirm) {
        if (!confirmPin[index] && index > 0) {
          const prevInput = confirmInputRefs.current[index - 1];
          if (prevInput) prevInput.focus();
        }
      } else {
        if (!pin[index] && index > 0) {
          const prevInput = inputRefs.current[index - 1];
          if (prevInput) prevInput.focus();
        }
      }
    }
  };

  const validatePin = (): string | null => {
    const pinString = pin.join('');
    const confirmPinString = confirmPin.join('');
    
    if (pinString.length !== 4) {
      return 'PIN must be exactly 4 digits';
    }
    
    if (confirmPinString.length !== 4) {
      return 'Please confirm your PIN';
    }
    
    if (pinString !== confirmPinString) {
      return 'PINs do not match';
    }
    
    // Check for common weak PINs
    const weakPins = ['0000', '1111', '1234', '4321', '9999'];
    if (weakPins.includes(pinString)) {
      return 'Please choose a stronger PIN';
    }
    
    // Check for sequential numbers
    if (/0123|1234|2345|3456|4567|5678|6789|9876|8765|7654|6543|5432|4321|3210/.test(pinString)) {
      return 'Please avoid sequential numbers';
    }
    
    return null;
  };

  const handleContinue = () => {
    const pinString = pin.join('');
    if (pinString.length === 4) {
      setCurrentStep('confirm');
      // Focus first confirm input
      setTimeout(() => {
        if (confirmInputRefs.current[0]) {
          confirmInputRefs.current[0].focus();
        }
      }, 100);
    }
  };

  const handleSubmit = async () => {
    const validationError = validatePin();
    if (validationError) {
      setError(validationError);
      return;
    }
    
    try {
      setIsProcessing(true);
      setError('');
      
      const pinString = pin.join('');
      const success = await onPinSet(ticket.id, pinString);
      
      if (success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError('Failed to set PIN. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set PIN');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBack = () => {
    setCurrentStep('pin');
    setConfirmPin(['', '', '', '']);
    setError('');
  };

  const handleClose = () => {
    if (!isProcessing && !success) {
      onClose();
    }
  };

  const isPinComplete = pin.every(digit => digit !== '');
  const isConfirmComplete = confirmPin.every(digit => digit !== '');
  const canContinue = isPinComplete && currentStep === 'pin';
  const canSubmit = isPinComplete && isConfirmComplete && currentStep === 'confirm';

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <X size={20} />
        </CloseButton>

        <Header>
          <Title>
            <Shield size={32} />
            {isChangingPin ? 'Change PIN' : 'Set PIN'}
          </Title>
          <Subtitle>
            {isChangingPin 
              ? 'Enter a new 4-digit PIN for your ticket'
              : 'Set a secure 4-digit PIN to protect your ticket'
            }
          </Subtitle>
        </Header>

        <TicketInfo>
          <TicketClass class={ticket.ticketClass}>
            {ticket.ticketClass.toUpperCase()} CLASS
          </TicketClass>
          <TicketAmount>KES {ticket.amount.toLocaleString()}</TicketAmount>
        </TicketInfo>

        {currentStep === 'pin' && (
          <>
            <PinSection>
              <PinLabel>Enter your 4-digit PIN</PinLabel>
              
              <PinInputContainer>
                {pin.map((digit, index) => (
                  <PinInput
                    key={index}
                    ref={(el) => inputRefs.current[index] = el}
                    type={showPin ? 'text' : 'password'}
                    value={digit}
                    onChange={(e) => handlePinChange(index, e.target.value)}
                    onKeyDown={(e) => handlePinKeyDown(index, e)}
                    maxLength={1}
                    placeholder="0"
                    hasError={!!error}
                    isFocused={document.activeElement === inputRefs.current[index]}
                  />
                ))}
              </PinInputContainer>
              
              <PinToggle onClick={() => setShowPin(!showPin)}>
                {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
                {showPin ? ' Hide PIN' : ' Show PIN'}
              </PinToggle>
            </PinSection>

            <Instructions>
              <Info size={16} style={{ marginRight: '8px' }} />
              This PIN will be required to use your ticket in competitions. 
              Choose a PIN that you can remember but others cannot easily guess.
            </Instructions>

            <SecurityTips>
              <Key size={16} style={{ marginRight: '8px' }} />
              <strong>Security Tips:</strong> Avoid using birth dates, 
              sequential numbers (1234), or repeated digits (0000).
            </SecurityTips>
          </>
        )}

        {currentStep === 'confirm' && (
          <>
            <PinSection>
              <PinLabel>Confirm your 4-digit PIN</PinLabel>
              
              <PinInputContainer>
                {confirmPin.map((digit, index) => (
                  <PinInput
                    key={index}
                    ref={(el) => confirmInputRefs.current[index] = el}
                    type={showConfirmPin ? 'text' : 'password'}
                    value={digit}
                    onChange={(e) => handlePinChange(index, e.target.value, true)}
                    onKeyDown={(e) => handlePinKeyDown(index, e, true)}
                    maxLength={1}
                    placeholder="0"
                    hasError={!!error}
                    isFocused={document.activeElement === confirmInputRefs.current[index]}
                  />
                ))}
              </PinInputContainer>
              
              <PinToggle onClick={() => setShowConfirmPin(!showConfirmPin)}>
                {showConfirmPin ? <EyeOff size={16} /> : <Eye size={16} />}
                {showConfirmPin ? ' Hide PIN' : ' Show PIN'}
              </PinToggle>
            </PinSection>

            <Instructions>
              <Info size={16} style={{ marginRight: '8px' }} />
              Please re-enter your PIN to confirm. This helps prevent typos.
            </Instructions>
          </>
        )}

        {error && (
          <ErrorMessage>
            <AlertTriangle size={16} />
            {error}
          </ErrorMessage>
        )}

        {success && (
          <SuccessMessage>
            <CheckCircle size={16} />
            PIN set successfully! Closing in 2 seconds...
          </SuccessMessage>
        )}

        <ActionButtons>
          {currentStep === 'pin' ? (
            <>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              
              <Button
                variant="primary"
                onClick={handleContinue}
                disabled={!canContinue}
              >
                Continue
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={handleBack}>
                Back
              </Button>
              
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!canSubmit || isProcessing}
              >
                {isProcessing ? (
                  <>
                    <LoadingSpinner />
                    Setting PIN...
                  </>
                ) : (
                  <>
                    <Lock size={16} />
                    Set PIN
                  </>
                )}
              </Button>
            </>
          )}
        </ActionButtons>
      </ModalContent>
    </ModalOverlay>
  );
};

export default PinSetupModal;
